/**
 * Hub Registry - Types and environment URL helpers
 */

import type { HubId, HubCategory } from '../../components/types'

export interface HubDefinition {
  id: HubId
  name: string
  shortName: string
  description: string
  icon: any
  baseUrl: string
  category: HubCategory
  status: 'production' | 'development' | 'planned'
  accentColor: string
  accentGradient: string
  defaultLandingPage: string
  pages: Array<{
    id: string
    label: string
    href: string
    icon: any
    description?: string
    isPrimary?: boolean
  }>
}

// Dev ports for each hub (used in vite dev mode)
const DEV_HUB_PORTS: Record<string, number> = {
  jobs: 5174,
  crm: 5175,
  quote: 5183,
  qa: 3004,
  qdrive: 5176,
  workforce: 5177,
  email: 5182,
  billing: 5181,
  cfo: 5173,
  hr: 5185,
  teams: 5190,
  ai: 5184,
  developer: 5191,
  inventory: 5186,
  assets: 5187,
  governance: 5188,
  smartdocs: 5189,
}

// Production URLs for each hub
const PROD_HUB_URLS: Record<string, string> = {
  jobs: 'https://dashboard.quotech.ai',
  crm: 'https://crm.quotech.ai',
  quote: 'https://quote.quotech.ai',
  qa: 'https://qa.quotech.ai',
  qdrive: 'https://qdrive.quotech.ai',
  workforce: 'https://workforce.quotech.ai',
  email: 'https://email.quotech.ai',
  billing: 'https://billing.quotech.ai',
  cfo: 'https://cfo.quotech.ai',
  hr: 'https://hr.quotech.ai',
  teams: 'https://teams.quotech.ai',
  ai: 'https://ai.quotech.ai',
  developer: 'https://dashboard.quotech.ai/developer',
  inventory: 'https://inventory.quotech.ai',
  assets: 'https://assets.quotech.ai',
  // Compliance hub lives in-shell on the main dashboard (no standalone subdomain),
  // mirroring the `developer` hub. Hub-switcher links resolve to /compliance/*.
  governance: 'https://dashboard.quotech.ai/compliance',
  // In-shell on the main dashboard (no standalone subdomain), mirroring the
  // `developer` hub. Hub-switcher links resolve to /smartdocs/*.
  smartdocs: 'https://dashboard.quotech.ai/smartdocs',
}

/**
 * Get the correct hub URL based on environment.
 * In dev: localhost with port. In prod: *.quotech.ai domain.
 */
export function getHubUrl(hubId: string): string {
  const isDev = typeof import.meta !== 'undefined' && import.meta.env?.DEV
  if (isDev && DEV_HUB_PORTS[hubId]) {
    return `http://localhost:${DEV_HUB_PORTS[hubId]}`
  }
  return PROD_HUB_URLS[hubId] || `https://${hubId}.quotech.ai`
}
